import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Paper, Stack, TextField, Chip,
  CircularProgress, Autocomplete, Dialog, DialogContent,
  Divider, IconButton, Snackbar, Alert, Tooltip, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Select, MenuItem, FormControl, Fade, Zoom,
  InputAdornment, Skeleton, useTheme, Checkbox,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FolderIcon from "@mui/icons-material/Folder";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { getCurrencySymbol, fmtL } from "../../../utils/currency";
import {
  getWatchlistById, getAllWatchlists, addStockToWatchlist,
  createWatchlist, removeStockFromWatchlist,
} from "../../../api/watchlist_api";
import { searchStockName } from "../../../api/api";
import { frontierAnalysis, efAddToPortfolio } from "../../../api/portfolio_api";
import { fmtNumber } from "../Portfolio/portfolioHelpers";
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis,
  Tooltip as RechartsTooltip, Cell, PieChart, Pie, CartesianGrid,
} from "recharts";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const MAX_NAME = 50;
const today = new Date().toISOString().split("T")[0];

const market = sessionStorage.getItem("selectedMarket") ?? "US";
const marketIsAll = market === "ALL";

const MARKET_DEFAULT_RFR = {
  US: 4.3,
  TR: 28,
};

const DEFAULT_RFR = MARKET_DEFAULT_RFR[market] ?? 4.3;   // fallback for unknown markets
const MIN_RFR = DEFAULT_RFR;

/* ─────────────────────────────────────────────
   CUSTOM SCATTER TOOLTIP
───────────────────────────────────────────── */
const CustomTooltip = ({ active, payload }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Paper elevation={8} sx={{
      bgcolor: isDark ? "rgba(31,41,55,0.98)" : "rgba(255,255,255,0.98)",
      p: 2, border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: 2, backdropFilter: "blur(10px)",
    }}>
      <Typography color={isDark ? "#fbbf24" : "#f59e0b"} fontWeight={700} fontSize="var(--font-size-base)" mb={0.5}>
        Sharpe Ratio: {fmtL(d.sharpe, 2)}
      </Typography>
      <Divider sx={{ borderColor: theme.palette.divider, my: 0.5 }} />
      <Typography color="#10b981" fontWeight={600} fontSize="var(--font-size-base)">
        Expected Return: {fmtL(d.y * 100, 2)}%
      </Typography>
      <Typography color="#ef4444" fontWeight={600} fontSize="var(--font-size-base)">
        Volatility: {fmtL(d.x * 100, 2)}%
      </Typography>
    </Paper>
  );
};

/* ─────────────────────────────────────────────
   PIE TOOLTIP
───────────────────────────────────────────── */
const PieTooltip = ({ active, payload }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{
      bgcolor: isDark ? "rgba(31,41,55,0.98)" : "rgba(255,255,255,0.98)",
      p: 1.5, border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1,
    }}>
      <Typography color={theme.palette.text.primary} fontWeight={700} fontSize="var(--font-size-base)">
        {payload[0].name}
      </Typography>
      <Typography color={theme.palette.primary.main} fontWeight={600} fontSize="var(--font-size-sm)">
        {fmtL(payload[0].value * 100, 2)}%
      </Typography>
    </Paper>
  );
};

/* ─────────────────────────────────────────────
   CREATE PORTFOLIO FROM FRONTIER DIALOG
───────────────────────────────────────────── */
const CreatePortfolioFromFrontier = ({ open, onClose, weights, stockStats, portfolioTitle, portfolioType, accentColor, onCreated, setSnackbar }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [name, setName] = useState("");
  const [investmentAmount, setInvestmentAmount] = useState("5000");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [holdings, setHoldings] = useState([]);
  const sym = getCurrencySymbol();

  useEffect(() => {
    if (open && weights) {
      setName(`${portfolioTitle} Portfolio`);
      setError("");
      setInvestmentAmount("5000");
      setHoldings(
        Object.entries(weights).map(([ticker, weight]) => {
          const stat = stockStats.find(s => s.ticker === ticker);
          return {
            ticker,
            company_name: stat?.company_name || ticker,
            current_price: stat?.current_price || 0,
            weight,
            selected: true,
          };
        })
      );
    }
  }, [open, weights, stockStats, portfolioTitle]);

  const toggleAll = () => {
    const allSel = holdings.every(h => h.selected);
    setHoldings(holdings.map(h => ({ ...h, selected: !allSel })));
  };

  const toggleStock = ticker =>
    setHoldings(holdings.map(h => h.ticker === ticker ? { ...h, selected: !h.selected } : h));

  const handleInvestmentChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^\d*\.?\d*$/.test(v)) setInvestmentAmount(v);
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || trimmedName.length < 2) {
      setError("Portfolio name must be at least 2 characters");
      return;
    }
    const amount = parseFloat(investmentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid investment amount");
      return;
    }
    const selected = holdings.filter(h => h.selected);
    if (!selected.length) { setError("Select at least one stock"); return; }

    const totalWeight = selected.reduce((sum, h) => sum + h.weight, 0);

    const payload = {
      portfolio_name: trimmedName,
      investment_amount: amount,
      portfolio_type: portfolioType,
      stocks: selected.map(h => ({
        ticker: h.ticker,
        weight_pct: parseFloat(((h.weight / totalWeight) * 100).toFixed(2)),
        price: h.current_price,
      })),
    };

    setLoading(true);
    setError("");
    try {
      const res = await efAddToPortfolio(payload);
      if (res?.success && res.data?.response_status === "SUCCESS") {
        const respData = res.data.response_data;
        const failedCount = respData?.total_stocks_failed || 0;
        const addedCount = respData?.total_stocks_added || 0;
        const mergedCount = respData?.total_stocks_merged || 0;

        if (failedCount > 0 && addedCount === 0 && mergedCount === 0) {
          setError("All stocks failed to add. Check ticker validity.");
          setLoading(false);
          return;
        }

        let message = `Portfolio "${trimmedName}" created successfully`;
        if (failedCount > 0) {
          message += ` (${failedCount} stock${failedCount > 1 ? "s" : ""} failed)`;
        }

        setSnackbar?.({ message, severity: failedCount > 0 ? "warning" : "success" });
        onCreated?.(respData);
        onClose();
      } else {
        setError(res?.data?.response_message || res?.error?.response?.data?.response_message || "Failed to create portfolio");
      }
    } catch {
      setError("Failed to create portfolio");
    }
    setLoading(false);
  };

  const selectedCount = holdings.filter(h => h.selected).length;
  const allSelected = holdings.length > 0 && holdings.every(h => h.selected);
  const investmentNum = parseFloat(investmentAmount) || 0;

  const selectedHoldings = holdings.filter(h => h.selected);
  const totalWeight = selectedHoldings.reduce((sum, h) => sum + h.weight, 0);

  const fieldSx = (selected) => ({
    fontSize: 13,
    borderRadius: 1,
    bgcolor: selected ? (isDark ? "#1e293b" : "var(--color-surface)") : (isDark ? "#0b1120" : "var(--color-bg-secondary)"),
    color: theme.palette.text.primary,
    "& fieldset": { borderColor: isDark ? "#334155" : "var(--color-border-primary)" },
    "&:hover fieldset": { borderColor: selected ? (isDark ? "#475569" : "var(--color-border-secondary)") : (isDark ? "#334155" : "var(--color-border-primary)") },
    "&.Mui-focused fieldset": { borderColor: "var(--color-primary)", borderWidth: "1px" },
    "&.Mui-disabled": { bgcolor: isDark ? "#0b1120" : "var(--color-bg-secondary)", "& fieldset": { borderColor: isDark ? "#1e293b" : "var(--color-border-primary)" } },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? "var(--color-surface)" : "var(--color-surface)",
          backgroundImage: "none",
          borderRadius: 1.5,
          border: `1px solid ${isDark ? "#1e293b" : "var(--color-border-primary)"}`,
          borderTop: `3px solid ${accentColor}`,
          boxShadow: "var(--shadow-xl)",
          m: { xs: 2, sm: 3 },
          maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 48px)" },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* HEADER */}
        <Box sx={{
          px: 2.5, pt: 1.75, pb: 1.5,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: `1px solid ${isDark ? "#1e293b" : "var(--color-border-primary)"}`,
        }}>
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1,
              bgcolor: isDark ? "#1e293b" : "var(--color-surface-hover)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FolderIcon sx={{ fontSize: 16, color: accentColor }} />
            </Box>
            <Box>
              <Typography fontSize="var(--font-size-base)" fontWeight={700} color={theme.palette.text.primary}>
                Create Portfolio from Frontier
              </Typography>
              <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary}>
                {portfolioTitle} · weights pre-filled from simulation
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{
            color: theme.palette.text.secondary, p: 0.5,
            "&:hover": { bgcolor: isDark ? "#1e293b" : "var(--color-surface-hover)" },
          }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 2.5, py: 2.5 }}>
          {/* PORTFOLIO NAME */}
          <Box mb={2}>
            <Typography fontSize="var(--font-size-sm)" fontWeight={600} color={theme.palette.text.primary} mb={0.75}>
              Portfolio name
            </Typography>
            <TextField
              fullWidth size="small" autoFocus
              placeholder="e.g., Max Sharpe Portfolio"
              value={name}
              inputProps={{ maxLength: MAX_NAME }}
              onChange={e => { setName(e.target.value); setError(""); }}
              error={!!error && error.includes("name")}
              InputProps={{
                sx: {
                  ...fieldSx(true),
                  "& fieldset": { borderColor: (error && error.includes("name")) ? "var(--color-error)" : (isDark ? "#334155" : "var(--color-border-primary)") },
                  "&.Mui-focused fieldset": { borderColor: (error && error.includes("name")) ? "var(--color-error)" : "var(--color-primary)", borderWidth: "1px" },
                },
              }}
              helperText={
                <Box component="span" sx={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--color-text-muted)" }}>Choose a name for your portfolio</span>
                  <span style={{ color: "var(--color-text-muted)" }}>{name.length}/{MAX_NAME}</span>
                </Box>
              }
              FormHelperTextProps={{ sx: { mx: 0, mt: 0.75, fontSize: 11 } }}
            />
          </Box>

          {/* TOTAL INVESTMENT AMOUNT */}
          <Box mb={2.5}>
            <Typography fontSize="var(--font-size-sm)" fontWeight={600} color={theme.palette.text.primary} mb={0.75}>
              Total Investment Amount
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder="e.g., 5000"
              value={investmentAmount}
              onChange={handleInvestmentChange}
              error={!!error && error.includes("investment")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography fontSize="var(--font-size-sm)" color={theme.palette.text.secondary} fontWeight={600}>{sym}</Typography>
                  </InputAdornment>
                ),
                sx: {
                  ...fieldSx(true),
                  "& fieldset": { borderColor: (error && error.includes("investment")) ? "var(--color-error)" : (isDark ? "#334155" : "var(--color-border-primary)") },
                  "&.Mui-focused fieldset": { borderColor: (error && error.includes("investment")) ? "var(--color-error)" : "var(--color-primary)", borderWidth: "1px" },
                },
              }}
              helperText={
                <span style={{ color: "var(--color-text-muted)" }}>
                  Amount to invest across selected stocks based on weights
                </span>
              }
              FormHelperTextProps={{ sx: { mx: 0, mt: 0.75, fontSize: 11 } }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{error}</Alert>
          )}

          <Divider sx={{ my: 2, borderColor: isDark ? "#1e293b" : "var(--color-border-primary)" }} />

          {/* STOCK SELECTION HEADER */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography fontSize="var(--font-size-sm)" fontWeight={700} color={theme.palette.text.secondary}
              textTransform="uppercase" letterSpacing="0.06em">
              Stocks ({selectedCount}/{holdings.length})
            </Typography>
            <Button size="small" onClick={toggleAll} sx={{
              fontSize: 12, fontWeight: 600, color: "var(--color-primary)",
              textTransform: "none", p: 0.5,
              "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
            }}>
              {allSelected ? "Unselect All" : "Select All"}
            </Button>
          </Box>

          {/* STOCK LIST */}
          <Box sx={{
            maxHeight: 300, overflowY: "auto",
            bgcolor: isDark ? "#0b1120" : "var(--color-bg-secondary)",
            borderRadius: 1, border: `1px solid ${isDark ? "#1e293b" : "var(--color-border-primary)"}`,
            mb: 2,
          }}>
            {holdings.map((h, idx) => {
              const weightPct = totalWeight > 0 ? (h.weight / totalWeight) : 0;
              const allocatedAmount = h.selected ? investmentNum * weightPct : 0;
              const estimatedShares = h.current_price > 0 ? allocatedAmount / h.current_price : 0;

              return (
                <Box key={h.ticker} sx={{
                  p: 1.5,
                  borderBottom: idx < holdings.length - 1
                    ? `1px solid ${isDark ? "#1e293b" : "var(--color-surface)"}`
                    : "none",
                  display: "flex", alignItems: "center", gap: 1.5,
                  transition: "all 0.15s ease",
                  opacity: h.selected ? 1 : 0.5,
                  "&:hover": { bgcolor: isDark ? "#1e293b" : "var(--color-surface)" },
                }}>
                  <Checkbox
                    checked={h.selected}
                    onChange={() => toggleStock(h.ticker)}
                    icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                    checkedIcon={<CheckBoxIcon sx={{ fontSize: 18, color: accentColor }} />}
                    sx={{ color: theme.palette.text.secondary, p: 0, "&.Mui-checked": { color: accentColor } }}
                  />

                  <Box flex={1} minWidth={0}>
                    <Typography fontSize="var(--font-size-base)" fontWeight={700} color={theme.palette.text.primary}>
                      {h.ticker}
                    </Typography>
                    <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary} noWrap>
                      {h.company_name}
                    </Typography>
                  </Box>

                  <Chip
                    label={`${fmtL(h.weight * 100, 1)}%`}
                    size="small"
                    sx={{
                      height: 22, fontSize: 11, fontWeight: 700,
                      bgcolor: h.selected ? `${accentColor}22` : (isDark ? "#1e293b" : "var(--color-bg-tertiary)"),
                      color: h.selected ? accentColor : theme.palette.text.secondary,
                      border: `1px solid ${h.selected ? accentColor : "transparent"}`,
                    }}
                  />

                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 80 }}>
                    <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary} mb={0.25}>Price</Typography>
                    <Typography fontSize="var(--font-size-sm)" fontWeight={600} color={theme.palette.text.primary}>
                      {sym}{fmtNumber(h.current_price)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 90 }}>
                    <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary} mb={0.25}>Allocated</Typography>
                    <Typography fontSize="var(--font-size-sm)" fontWeight={600}
                      color={h.selected ? "var(--color-success)" : theme.palette.text.secondary}>
                      {sym}{fmtNumber(allocatedAmount)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 70 }}>
                    <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary} mb={0.25}>Est. Shares</Typography>
                    <Typography fontSize="var(--font-size-sm)" fontWeight={600} color={theme.palette.text.primary}>
                      {h.selected ? fmtNumber(estimatedShares, 2) : "—"}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* SUMMARY */}
          {selectedCount > 0 && (
            <Box sx={{
              bgcolor: isDark ? "#1e293b" : "var(--color-surface-hover)",
              borderRadius: 1, p: 1.5, mb: 2,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <Box>
                <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary} mb={0.25}>Selected Holdings</Typography>
                <Typography fontSize="var(--font-size-base)" fontWeight={600} color={theme.palette.text.primary}>
                  {selectedCount} stock{selectedCount !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary} mb={0.25}>Portfolio Type</Typography>
                <Chip
                  label={portfolioType === "max_sharpe" ? "Max Sharpe" : "Min Volatility"}
                  size="small"
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 700,
                    bgcolor: `${accentColor}22`, color: accentColor,
                    border: `1px solid ${accentColor}`,
                  }}
                />
              </Box>
              <Box textAlign="right">
                <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary} mb={0.25}>Total Investment</Typography>
                <Typography fontSize="var(--font-size-base)" fontWeight={700} color="var(--color-success)">
                  {sym}{fmtNumber(investmentNum)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* ACTIONS */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined" onClick={onClose} disabled={loading} fullWidth
              sx={{
                textTransform: "none", fontSize: 13, fontWeight: 500,
                color: theme.palette.text.secondary,
                borderColor: isDark ? "#334155" : "var(--color-border-primary)",
                "&:hover": { bgcolor: isDark ? "#1e293b" : "var(--color-surface-hover)" },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth variant="contained"
              disabled={loading || name.trim().length < 2 || selectedCount === 0 || investmentNum <= 0}
              onClick={handleCreate}
              sx={{
                textTransform: "none", fontSize: 13, fontWeight: 600,
                bgcolor: accentColor, boxShadow: "none",
                "&:hover": { opacity: 0.88, boxShadow: "none" },
                "&.Mui-disabled": {
                  bgcolor: isDark ? "#1e293b" : "var(--color-surface-hover)",
                  color: theme.palette.text.secondary,
                },
              }}
            >
              {loading
                ? <CircularProgress size={14} color="inherit" />
                : `Create portfolio · ${sym}${fmtNumber(investmentNum)}`}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const EfficientFrontierSimulator = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [searchParams, setSearchParams] = useSearchParams();
  const watchlistIdFromUrl = searchParams.get("watchlistId");

  const [allWatchlists, setAllWatchlists] = useState([]);
  const [currentWatchlist, setCurrentWatchlist] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [riskFreeRate, setRiskFreeRate] = useState(String(DEFAULT_RFR));
  const [loading, setLoading] = useState(false);
  const [loadingWatchlist, setLoadingWatchlist] = useState(false);
  const [searchingStocks, setSearchingStocks] = useState(false);
  const [addingStock, setAddingStock] = useState(null);
  const [removingStock, setRemovingStock] = useState(null);
  const [running, setRunning] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [isCreateWLDialogOpen, setIsCreateWLDialogOpen] = useState(false);
  const [newWLName, setNewWLName] = useState("");
  const [snackbar, setSnackbar] = useState(null);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState(watchlistIdFromUrl || "");
  const [frontierData, setFrontierData] = useState([]);
  const [optimal, setOptimal] = useState({ maxSharpe: null, minVol: null, maxReturn: null });
  const [stockStats, setStockStats] = useState([]);
  const [correlations, setCorrelations] = useState([]);
  const [simulationParams, setSimulationParams] = useState(null);
  const [noOfSimulations, setNoOfSimulations] = useState("2000");
  const sym = getCurrencySymbol();

  const [pfDialog, setPfDialog] = useState({ open: false, weights: {}, portfolioTitle: "", portfolioType: "max_sharpe", accentColor: "#fbbf24" });

  useEffect(() => { loadInitialData(); }, []);

  useEffect(() => {
    if (selectedWatchlistId) {
      clearResults();
      loadWatchlistData(selectedWatchlistId);
      setSearchParams({ watchlistId: selectedWatchlistId });
    } else {
      setSearchParams({});
      setCurrentWatchlist(null);
      setStocks([]);
      setSelectedTickers([]);
      clearResults();
    }
  }, [selectedWatchlistId]);

  const clearResults = () => {
    setFrontierData([]);
    setOptimal({ maxSharpe: null, minVol: null, maxReturn: null });
    setStockStats([]);
    setCorrelations([]);
    setSimulationParams(null);
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const wlRes = await getAllWatchlists();
      if (wlRes.success) setAllWatchlists(wlRes.data);
      if (watchlistIdFromUrl) await loadWatchlistData(watchlistIdFromUrl);
    } catch { setSnackbar({ message: "Failed to load watchlists", severity: "error" }); }
    finally { setLoading(false); }
  };

  const loadWatchlistData = async (id) => {
    setLoadingWatchlist(true);
    try {
      const res = await getWatchlistById(id);
      if (res.success) {
        setCurrentWatchlist(res.data);
        setStocks(res.data.stocks.map(s => ({ ticker: s.ticker, name: s.company_name })));
        setSelectedTickers(res.data.stocks.map(s => s.ticker));
      }
    } catch { setSnackbar({ message: "Failed to load watchlist", severity: "error" }); }
    finally { setLoadingWatchlist(false); }
  };

  const handleSearchChange = async (e, value) => {
    setSearchInput(value);
    if (value?.length > 0) {
      setSearchingStocks(true);
      try {
        const res = await searchStockName(value);
        if (res.success) setSearchOptions(res.data);
      } catch { setSnackbar({ message: "Stock search failed", severity: "error" }); }
      finally { setSearchingStocks(false); }
    } else { setSearchOptions([]); }
  };

  const addTicker = async (stock) => {
    if (stocks.some(s => s.ticker === stock.ticker)) {
      setSnackbar({ message: `${stock.ticker} already added`, severity: "warning" });
      return;
    }
    setAddingStock(stock.ticker);
    try {
      if (selectedWatchlistId) {
        const res = await addStockToWatchlist(selectedWatchlistId, { ticker: stock.ticker });
        if (res.success) {
          setStocks(p => [...p, { ticker: stock.ticker, name: stock.company_name }]);
          setSelectedTickers(p => [...p, stock.ticker]);
          setSnackbar({ message: `${stock.ticker} added successfully`, severity: "success" });
        } else { setSnackbar({ message: `Failed to add ${stock.ticker}`, severity: "error" }); }
      } else {
        setStocks(p => [...p, { ticker: stock.ticker, name: stock.company_name }]);
        setSelectedTickers(p => [...p, stock.ticker]);
        setSnackbar({ message: `${stock.ticker} added`, severity: "success" });
      }
    } catch { setSnackbar({ message: `Error adding ${stock.ticker}`, severity: "error" }); }
    finally { setAddingStock(null); setSearchInput(""); }
  };

  const removeTicker = async (ticker) => {
    setRemovingStock(ticker);
    try {
      if (selectedWatchlistId) {
        const res = await removeStockFromWatchlist(selectedWatchlistId, ticker);
        if (res.success) {
          setStocks(p => p.filter(s => s.ticker !== ticker));
          setSelectedTickers(p => p.filter(t => t !== ticker));
          setSnackbar({ message: `${ticker} removed`, severity: "info" });
        } else { setSnackbar({ message: `Failed to remove ${ticker}`, severity: "error" }); }
      } else {
        setStocks(p => p.filter(s => s.ticker !== ticker));
        setSelectedTickers(p => p.filter(t => t !== ticker));
        setSnackbar({ message: `${ticker} removed`, severity: "info" });
      }
    } catch { setSnackbar({ message: `Error removing ${ticker}`, severity: "error" }); }
    finally { setRemovingStock(null); }
  };

  const handleCreateWatchlist = async () => {
    if (!newWLName.trim()) return;
    setLoading(true);
    try {
      const res = await createWatchlist({ name: newWLName.trim() });
      if (res.success) {
        setIsCreateWLDialogOpen(false);
        setSelectedWatchlistId(res.data.id);
        setAllWatchlists(p => [...p, res.data]);
        setSnackbar({ message: `Watchlist "${newWLName}" created`, severity: "success" });
        setNewWLName("");
      } else { setSnackbar({ message: "Failed to create watchlist", severity: "error" }); }
    } catch { setSnackbar({ message: "Error creating watchlist", severity: "error" }); }
    finally { setLoading(false); }
  };

  const handleRiskFreeRateChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^\d*\.?\d*$/.test(v)) setRiskFreeRate(v);
  };

  const handleSimulationsChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^\d+$/.test(v)) setNoOfSimulations(v);
  };

  const runSimulation = async () => {
    const n = parseInt(noOfSimulations);
    // ── Validation: show error instead of silently returning ──
    if (isNaN(n) || n < 1000 || n > 3000) {
      setSnackbar({ message: "Number of simulations must be between 1,000 and 3,000", severity: "error" });
      return;
    }
    let rate = parseFloat(riskFreeRate);
    if (riskFreeRate === "" || isNaN(rate)) {
      rate = DEFAULT_RFR;
    } else if (rate < MIN_RFR) {
      setSnackbar({ message: `Risk-free rate must be at least ${MIN_RFR}%`, severity: "error" });
      return;
    }
    if (selectedTickers.length < 2) {
      setSnackbar({ message: "Please select at least 2 stocks", severity: "warning" });
      return;
    }
    setRunning(true);
    try {
      const res = await frontierAnalysis({ tickers: selectedTickers, period_years: 1, num_portfolios: n, risk_free_rate: rate / 100 });
      if (res.success) {
        setFrontierData(res.data.simulated_portfolios.map(p => ({ x: p.volatility, y: p.expected_return, sharpe: p.sharpe_ratio })));
        setOptimal({
          maxSharpe: res.data.optimal_portfolios.find(p => p.name === "max_sharpe"),
          minVol: res.data.optimal_portfolios.find(p => p.name === "min_volatility"),
          maxReturn: res.data.optimal_portfolios.find(p => p.name === "max_return"),
        });
        setStockStats(res.data.stock_stats || []);
        setCorrelations(res.data.correlations || []);
        setSimulationParams({ period_years: res.data.period_years, num_portfolios: res.data.num_portfolios, risk_free_rate: res.data.risk_free_rate, trading_days: res.data.trading_days_used });
        setSnackbar({ message: "Simulation completed successfully", severity: "success" });
      } else { setSnackbar({ message: "Simulation failed", severity: "error" }); }
    } catch { setSnackbar({ message: "Error running simulation", severity: "error" }); }
    finally { setRunning(false); }
  };

  const getSharpeColor = s => s > 0.3 ? "#10b981" : s > 0.15 ? "#3b82f6" : s > 0 ? "#8b5cf6" : "#64748b";
  // const showInsufficientAlert = selectedWatchlistId && selectedTickers.length < 2;
  const showInsufficientAlert = selectedWatchlistId && !loadingWatchlist && selectedTickers.length < 2;
  // ── Derived validation state ──────────────────────────────────────────────
  const simCount = parseInt(noOfSimulations);
  const simInvalid = !noOfSimulations || isNaN(simCount) || simCount < 1000 || simCount > 3000;
  const simFieldError = noOfSimulations !== "" && simInvalid;           // show red UI only when something is typed

  const rateVal = parseFloat(riskFreeRate);
  const rateInvalid = riskFreeRate !== "" && !isNaN(rateVal) && rateVal < MIN_RFR;
  const rateFieldError = rateInvalid;

  // Button is active only when all params are valid
  const canRun = selectedTickers.length >= 2 && !simInvalid && !rateInvalid;
  // ─────────────────────────────────────────────────────────────────────────

  const getDisabledReasons = () => {
    const reasons = [];
    if (selectedTickers.length < 2)
      reasons.push(`Add at least 2 stocks (currently ${selectedTickers.length})`);
    if (simInvalid)
      reasons.push("Simulations must be between 1,000 and 3,000");
    if (rateInvalid)                          // ← only shown when a bad value is typed
      reasons.push(`Risk-free rate must be at least ${MIN_RFR}%`);
    return reasons;
  };

  const openPfDialog = (portfolioTitle, weights, accentColor, portfolioType) =>
    setPfDialog({ open: true, portfolioTitle, weights, accentColor, portfolioType });

  const paperBorder = `1px solid ${isDark ? "#1e293b" : "var(--color-border-primary)"}`;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default, px: { xs: 2, md: 4 }, py: 3.5 }}>

      {/* PAGE HEADER */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box mb={3.5}>
          <Typography
            variant="title"
            fontWeight="var(--font-weight-bold)"
            sx={{ color: 'var(--color-text-primary)', display: 'block' }}
            letterSpacing="-0.02em"
            mb={0.5}
          >
            Efficient Frontier Simulator
          </Typography>
          <Typography variant="subtitle" fontWeight="var(--font-weight-normal)" sx={{ color: 'var(--color-text-muted)' }}>
            Portfolio optimization using Modern Portfolio Theory
          </Typography>
        </Box>
        <Tooltip
          title={
            !canRun && !running ? (
              <Box sx={{ p: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-bold)",
                    color: "#fbbf24",
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Cannot run simulation
                </Typography>
                {getDisabledReasons().map((r, i) => (
                  <Typography
                    key={i}
                    sx={{
                      fontSize: "var(--font-size-xs)",
                      color: "#f8fafc",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                      mt: 0.25,
                    }}
                  >
                    • {r}
                  </Typography>
                ))}
              </Box>
            ) : ""
          }
          placement="bottom-end"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: isDark ? "#1e293b" : "var(--color-surface)",
                border: "1px solid #f59e0b",
                borderRadius: "var(--radius-md)",
                boxShadow: "var(--shadow-lg)",
                maxWidth: 260,
                p: 1.25,
              },
            },
            arrow: { sx: { color: "#f59e0b" } },
          }}
        >
          {/* Box wrapper is required — disabled buttons don't fire mouse events */}
          <Box
            component="span"
            onClick={() => {
              if (!canRun && !running) {
                const reasons = getDisabledReasons();
                if (reasons.length) {
                  setSnackbar({
                    message: reasons.join(" · "),
                    severity: "warning",
                  });
                }
              }
            }}
            sx={{ display: "inline-flex", cursor: !canRun ? "not-allowed" : "default" }}
          >
            <Button
              size="large"
              variant="contained"
              onClick={runSimulation}
              disabled={!canRun || running}
              startIcon={
                running
                  ? <CircularProgress size={18} color="inherit" />
                  : <PlayArrowIcon />
              }
              sx={{
                px: 2.5, py: 1, whiteSpace: "nowrap",
                pointerEvents: !canRun ? "none" : "auto", // let Box handle the click when disabled
                background: canRun
                  ? "linear-gradient(135deg,#667eea 0%,#764ba2 100%)"
                  : (isDark ? "#334155" : "#E5E7EB"),
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-semibold)",
                boxShadow: canRun ? "0 4px 12px rgba(102,126,234,0.3)" : "none",
                "&:hover": {
                  boxShadow: canRun ? "0 6px 16px rgba(102,126,234,0.5)" : "none",
                  transform: canRun ? "translateY(-1px)" : "none",
                },
                transition: "all 0.2s ease",
              }}
            >
              {running ? "Running Simulation..." : "Run Simulation"}
            </Button>
          </Box>
        </Tooltip>
      </Box>

      {showInsufficientAlert && (
        <Fade in>
          <Alert severity="warning" icon={<WarningAmberIcon fontSize="medium" />} sx={{
            mb: 3, bgcolor: "rgba(245,158,11,0.1)", border: "1px solid #f59e0b", borderRadius: 2,
            "& .MuiAlert-icon": { color: "#f59e0b" },
          }}>
            <Typography fontSize="var(--font-size-base)" fontWeight={700} color="#f59e0b" mb={0.5}>
              Insufficient Tickers in Watchlist
            </Typography>
            <Typography fontSize="var(--font-size-base)" color={isDark ? "#fbbf24" : "#f59e0b"}>
              Please make sure to have at least 2 tickers. Current watchlist has {selectedTickers.length} ticker{selectedTickers.length === 1 ? "" : "s"}.
            </Typography>
          </Alert>
        </Fade>
      )}

      <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "260px 1fr" }} gap={3}>

        {/* ── LEFT COLUMN ── */}
        <Box>
          <Stack spacing={2}>
            {/* WATCHLIST SELECTOR */}
            <Paper sx={{ bgcolor: theme.palette.background.paper, p: 2.5, borderRadius: 2, border: paperBorder }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography fontSize="var(--font-size-sm)" fontWeight={700} color={theme.palette.text.secondary} textTransform="uppercase" letterSpacing="0.1em">
                  Configure Watchlist
                </Typography>
                <Tooltip title="Create new watchlist">
                  <IconButton size="small" onClick={() => setIsCreateWLDialogOpen(true)} disabled={loading} sx={{ color: theme.palette.primary.main }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedWatchlistId}
                  onChange={e => setSelectedWatchlistId(e.target.value)}
                  displayEmpty disabled={loading || loadingWatchlist}
                  sx={{
                    bgcolor: isDark ? "#1e293b" : "var(--color-bg-secondary)",
                    color: theme.palette.text.primary, fontSize: 15,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#334155" : "var(--color-border-primary)" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#475569" : "var(--color-border-secondary)" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                  }}
                  MenuProps={{ PaperProps: { sx: { bgcolor: theme.palette.background.paper, border: `1px solid ${isDark ? "#334155" : "var(--color-border-primary)"}`, maxHeight: 240, "& .MuiMenuItem-root": { color: theme.palette.text.primary, fontSize: 15, "&:hover": { bgcolor: isDark ? "#334155" : "var(--color-surface-hover)" } } } } }}
                >
                  <MenuItem value=""><em>No Watchlist Selected</em></MenuItem>
                  {allWatchlists.map(wl => <MenuItem key={wl.id} value={wl.id}>{wl.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Paper>

            {/* STOCK SEARCH */}
            {!marketIsAll && (
              <Paper sx={{ bgcolor: theme.palette.background.paper, p: 2.5, borderRadius: 2, border: paperBorder }}>
                <Typography fontSize="var(--font-size-sm)" fontWeight={700} color={theme.palette.text.secondary} textTransform="uppercase" letterSpacing="0.1em" mb={2}>
                  Add Stocks
                </Typography>
                <Autocomplete
                  options={searchOptions}
                  getOptionLabel={o => `${o.ticker} - ${o.company_name}`}
                  inputValue={searchInput}
                  onInputChange={handleSearchChange}
                  onChange={(e, val) => val && addTicker(val)}
                  loading={searchingStocks}
                  disabled={addingStock !== null}
                  noOptionsText="Start typing ticker name here..."
                  loadingText={<Box display="flex" justifyContent="center" py={3}><CircularProgress size={30} sx={{ color: theme.palette.primary.main }} /></Box>}
                  componentsProps={{ paper: { sx: { bgcolor: theme.palette.background.paper, border: `1px solid ${isDark ? "#334155" : "var(--color-border-primary)"}`, borderRadius: 1.5, mt: 0.5, boxShadow: isDark ? "0 10px 25px rgba(0,0,0,0.5)" : "var(--shadow-lg)", "& .MuiAutocomplete-noOptions": { color: `${theme.palette.text.primary} !important`, fontStyle: "italic", fontSize: 11, textAlign: "center", py: 2 }, "& .MuiAutocomplete-loading": { color: `${theme.palette.text.primary} !important`, py: 0 } } } }}
                  renderInput={params => (
                    <TextField {...params} placeholder="Search ticker or company..." size="small"
                      InputProps={{ ...params.InputProps, startAdornment: <SearchIcon sx={{ color: theme.palette.text.secondary, mr: 1, fontSize: 18 }} />, endAdornment: <>{searchingStocks ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${isDark ? "#1e293b" : "var(--color-border-primary)"}`, "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: `${isDark ? "#1e293b" : "var(--color-surface-hover)"} !important` } }}>
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <Typography fontSize="var(--font-size-base)" fontWeight={700} color={theme.palette.primary.main} sx={{ minWidth: 70 }}>{option.ticker}</Typography>
                        <Typography fontSize="var(--font-size-base)" color={theme.palette.text.secondary} noWrap flex={1}>{option.company_name}</Typography>
                      </Box>
                    </Box>
                  )}
                  ListboxProps={{ sx: { maxHeight: 280, bgcolor: theme.palette.background.paper, py: 0 } }}
                />
              </Paper>
            )}

            {/* STOCKS LIST — always visible, with market-ALL info banner */}
            <Paper sx={{ bgcolor: theme.palette.background.paper, p: 2.5, borderRadius: 2, border: paperBorder }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography fontSize="var(--font-size-sm)" fontWeight={700} color={theme.palette.text.secondary} textTransform="uppercase" letterSpacing="0.1em">
                  Stocks ({selectedTickers.length})
                </Typography>
                {!marketIsAll && stocks.length > 0 && (
                  <Tooltip title="Clear all">
                    <IconButton size="small" onClick={() => { setStocks([]); setSelectedTickers([]); }} disabled={removingStock !== null} sx={{ color: "#ef4444" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {/* Banner shown only when market is ALL */}
              {marketIsAll && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 2,
                    bgcolor: "var(--color-info-light)",
                    color: "var(--color-text-primary)",
                    border: "1px solid var(--color-info)",
                    borderRadius: 1,
                    fontSize: "var(--font-size-sm)",
                    "& .MuiAlert-icon": { color: "var(--color-info)" },
                  }}
                >
                  To add or remove tickers, switch to a specific market.
                </Alert>
              )}

              {loadingWatchlist ? (
                <Stack spacing={1}>
                  {[1, 2, 3].map(i => (
                    <Box key={i} p={1.5} bgcolor={isDark ? "#1e293b" : "var(--color-bg-secondary)"} borderRadius={1.5}>
                      <Skeleton variant="text" width="40%" height={16} sx={{ bgcolor: isDark ? "#334155" : "var(--color-border-primary)" }} />
                      <Skeleton variant="text" width="70%" height={12} sx={{ bgcolor: isDark ? "#334155" : "var(--color-border-primary)", mt: 0.5 }} />
                    </Box>
                  ))}
                </Stack>
              ) : stocks.length === 0 ? (
                <Box textAlign="center" py={3}>
                  <Typography fontSize="var(--font-size-base)" color={theme.palette.text.secondary}>No stocks added yet</Typography>
                </Box>
              ) : (
                <Stack spacing={1} sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                  {stocks.map(s => (
                    <Zoom in key={s.ticker}>
                      <Box sx={{
                        p: 1.5,
                        bgcolor: selectedTickers.includes(s.ticker) ? (isDark ? "#1e293b" : "var(--color-primary-lighter)") : (isDark ? "#0a0f18" : "var(--color-bg-secondary)"),
                        borderRadius: 1.5,
                        border: selectedTickers.includes(s.ticker) ? `1px solid ${theme.palette.primary.main}` : `1px solid ${isDark ? "#1e293b" : "var(--color-border-primary)"}`,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        transition: "all 0.2s", opacity: removingStock === s.ticker ? 0.5 : 1,
                        "&:hover": { bgcolor: isDark ? "#1e293b" : "var(--color-surface-hover)", borderColor: isDark ? "#475569" : "var(--color-border-secondary)" },
                      }}>
                        <Box flex={1} minWidth={0}>
                          <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-bold)" color={theme.palette.text.primary} noWrap>{s.ticker}</Typography>
                          <Typography fontSize="var(--font-size-sm)" color={theme.palette.text.secondary} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</Typography>
                        </Box>

                        {/* Toggle & remove — hidden when market is ALL */}
                        {!marketIsAll && (
                          <Stack direction="row" spacing={0.5}>
                            <Chip
                              label={selectedTickers.includes(s.ticker) ? "✓" : "Add"}
                              size="small"
                              onClick={() => setSelectedTickers(p => p.includes(s.ticker) ? p.filter(t => t !== s.ticker) : [...p, s.ticker])}
                              disabled={removingStock === s.ticker}
                              sx={{ height: 24, fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", bgcolor: selectedTickers.includes(s.ticker) ? "#10b981" : (isDark ? "#334155" : "#E5E7EB"), color: selectedTickers.includes(s.ticker) ? "white" : theme.palette.text.primary, "&:hover": { bgcolor: selectedTickers.includes(s.ticker) ? "#059669" : (isDark ? "#475569" : "#D1D5DB") } }}
                            />
                            <IconButton size="small" onClick={() => removeTicker(s.ticker)} disabled={removingStock === s.ticker} sx={{ width: 24, height: 24, color: "#ef4444", "&:hover": { bgcolor: "rgba(239,68,68,0.1)" } }}>
                              {removingStock === s.ticker ? <CircularProgress size={14} sx={{ color: "#ef4444" }} /> : <CloseIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </Stack>
                        )}
                      </Box>
                    </Zoom>
                  ))}
                </Stack>
              )}
            </Paper>

            {/* SIMULATIONS */}
            <Paper sx={{ bgcolor: theme.palette.background.paper, p: 2.5, borderRadius: 2, border: paperBorder }}>
              <Typography fontSize="var(--font-size-sm)" fontWeight={700} color={theme.palette.text.secondary} textTransform="uppercase" letterSpacing="0.1em" mb={2}>
                No. of Simulations
              </Typography>
              <TextField
                fullWidth
                type="text"
                value={noOfSimulations}
                onChange={handleSimulationsChange}
                disabled={running}
                size="small"
                error={simFieldError}
                helperText={
                  simFieldError
                    ? "Must be between 1,000 and 3,000"
                    : "Allowed range: 1,000 – 3,000"
                }
                FormHelperTextProps={{
                  sx: {
                    mx: 0,
                    mt: 0.75,
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 600,
                    color: simFieldError ? "var(--color-error)" : theme.palette.text.secondary,
                  },
                }}
              />
            </Paper>

            {/* RISK-FREE RATE */}
            <Paper sx={{ bgcolor: theme.palette.background.paper, p: 2.5, borderRadius: 2, border: paperBorder }}>
              <Typography fontSize="var(--font-size-sm)" fontWeight={700} color={theme.palette.text.secondary} textTransform="uppercase" letterSpacing="0.1em" mb={2}>
                Risk-Free Rate
              </Typography>
              <TextField
                fullWidth
                type="text"
                placeholder={String(DEFAULT_RFR)}                    // ← shows default when empty
                value={riskFreeRate}
                onChange={handleRiskFreeRateChange}
                disabled={running}
                size="small"
                error={rateFieldError}
                helperText={
                  rateFieldError
                    ? `Rate must be at least ${MIN_RFR}%`
                    : riskFreeRate === ""
                      ? `Minimum rate for this market is ${MIN_RFR}%. Empty defaults to ${DEFAULT_RFR}%`
                      : `Minimum rate for this market is ${MIN_RFR}%`
                }
                FormHelperTextProps={{
                  sx: {
                    mx: 0,
                    mt: 0.75,
                    fontSize: "var(--font-size-sm)",
                    fontWeight: "var(--font-weight-bold)",
                    color: rateFieldError
                      ? "var(--color-error)"
                      : riskFreeRate === ""
                        ? "var(--color-primary)"           // ← blue hint when empty
                        : theme.palette.text.secondary,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        fontSize="var(--font-size-base)"
                        color={theme.palette.text.secondary}
                        fontWeight={600}
                      >
                        %
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Paper>
          </Stack>
        </Box>

        {/* ── RIGHT COLUMN ── */}
        <Box sx={{ minWidth: 0 }}>
          {loadingWatchlist ? (
            <Paper
              sx={{
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                border: paperBorder,
                minHeight: 500,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress
                size={48}
                thickness={3}
                sx={{ color: theme.palette.primary.main }}
              />
              <Box textAlign="center">
                <Typography
                  fontSize="var(--font-size-base)"
                  fontWeight="var(--font-weight-semibold)"
                  color={theme.palette.text.primary}
                  mb={0.5}
                >
                  Loading Watchlist
                </Typography>
                <Typography
                  fontSize="var(--font-size-sm)"
                  color={theme.palette.text.secondary}
                >
                  Fetching stocks and preparing simulation…
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {/* CHART */}
              <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: theme.palette.background.paper, borderRadius: 2, border: paperBorder, minHeight: 500, overflow: "hidden" }}>
                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" mb={2} gap={1}>
                  <Box style={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="title" fontWeight={700} color={theme.palette.text.primary}>Efficient Frontier</Typography>
                    <Typography variant="subtitle" color={theme.palette.text.secondary}>
                      {running ? "Computing optimal portfolios..." : frontierData.length > 0 ? `${frontierData.length} simulated portfolios` : "Run simulation to visualize"}
                    </Typography>
                  </Box>
                  {simulationParams && !running && (
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {[["PERIOD", `${simulationParams.period_years}Y`], ["PORTFOLIOS", simulationParams.num_portfolios.toLocaleString()], ["RISK-FREE RATE", `${fmtL(simulationParams.risk_free_rate * 100, 1)}%`]].map(([label, val]) => (
                        <Box key={label} textAlign="right">
                          <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary}>{label}</Typography>
                          <Typography fontSize="var(--font-size-base)" fontWeight={600} color={theme.palette.text.primary}>{val}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Stack>

                {running ? (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={400}>
                    <CircularProgress size={60} sx={{ color: "#667eea", mb: 3 }} />
                    <Typography fontSize="var(--font-size-base)" fontWeight={600} color={theme.palette.text.secondary} mb={1}>Running Simulation</Typography>
                    <Typography fontSize="var(--font-size-base)" color={theme.palette.text.secondary} textAlign="center" maxWidth={400}>Analyzing {selectedTickers.length} stocks...</Typography>
                  </Box>
                ) : frontierData.length === 0 ? (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={400}>
                    <ShowChartIcon sx={{ fontSize: 80, color: isDark ? "#1e293b" : "var(--color-border-primary)", mb: 2 }} />
                    <Typography fontSize="var(--font-size-base)" fontWeight={600} color={theme.palette.text.secondary} mb={1}>No Simulation Data</Typography>
                    <Typography fontSize="var(--font-size-base)" color={theme.palette.text.secondary} textAlign="center" maxWidth={400}>
                      Select at least 2 stocks and click "Run Simulation" to generate the efficient frontier
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ width: "100%", height: { xs: 260, sm: 350, md: 400 }, minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "var(--color-border-primary)"} />
                        <XAxis type="number" dataKey="x" stroke={theme.palette.text.secondary} tick={{ fill: theme.palette.text.secondary, fontSize: "var(--font-size-xs)" }} tickFormatter={v => `${fmtL(v * 100, 0)}%`} label={{ value: "Volatility (Risk)", position: "insideBottom", offset: -10, style: { fill: theme.palette.text.secondary, fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)" } }} />
                        <YAxis type="number" dataKey="y" stroke={theme.palette.text.secondary} tick={{ fill: theme.palette.text.secondary, fontSize: "var(--font-size-xs)" }} tickFormatter={v => `${fmtL(v * 100, 0)}%`} width={45} label={{ value: "Expected Return", angle: -90, position: "insideLeft", style: { fill: theme.palette.text.secondary, fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)" } }} />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Scatter data={frontierData} shape="circle">
                          {frontierData.map((entry, i) => <Cell key={i} fill={getSharpeColor(entry.sharpe)} fillOpacity={0.7} />)}
                        </Scatter>
                        {optimal.maxSharpe && <Scatter data={[{ x: optimal.maxSharpe.volatility, y: optimal.maxSharpe.expected_return, sharpe: optimal.maxSharpe.sharpe_ratio }]} fill="#fbbf24" shape="star" />}
                        {optimal.minVol && <Scatter data={[{ x: optimal.minVol.volatility, y: optimal.minVol.expected_return, sharpe: optimal.minVol.sharpe_ratio }]} fill="#10b981" shape="diamond" />}
                      </ScatterChart>
                    </ResponsiveContainer>
                  </Box>
                )}

                {frontierData.length > 0 && !running && (
                  <Stack direction="row" spacing={{ xs: 1, sm: 3 }} justifyContent="center" mt={2} flexWrap="wrap" gap={0.5}>
                    {[["#10b981", "High Sharpe (>0.3)"], ["#3b82f6", "Medium (0.15-0.3)"], ["#8b5cf6", "Low (0-0.15)"], ["#64748b", "Negative"]].map(([c, label]) => (
                      <Stack key={label} direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 10, height: 10, bgcolor: c, borderRadius: "50%", flexShrink: 0 }} />
                        <Typography fontSize="var(--font-size-sm)" color={theme.palette.text.secondary} noWrap>{label}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>

              {/* OPTIMAL PORTFOLIOS */}
              {running ? (
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={3}>
                  {[1, 2].map(i => (
                    <Paper key={i} sx={{ p: 3, bgcolor: theme.palette.background.paper, borderRadius: 2, border: paperBorder }}>
                      <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: isDark ? "#334155" : "var(--color-border-primary)", mb: 2 }} />
                      <Grid container spacing={2} mb={2}>
                        {[1, 2, 3].map(j => <Grid item xs={4} key={j}><Skeleton variant="rectangular" height={70} sx={{ bgcolor: isDark ? "#1e293b" : "var(--color-bg-secondary)", borderRadius: 1 }} /></Grid>)}
                      </Grid>
                      <Skeleton variant="rectangular" height={140} sx={{ bgcolor: isDark ? "#1e293b" : "var(--color-bg-secondary)", borderRadius: 1 }} />
                    </Paper>
                  ))}
                </Box>
              ) : optimal.maxSharpe && (
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={3}>
                  <OptimalPortfolioCard
                    title="Max Sharpe Ratio (Optimal Portfolio)"
                    subtitle="Best risk-adjusted return"
                    data={optimal.maxSharpe}
                    color="#fbbf24"
                    stockStats={stockStats}
                    onCreatePortfolio={() => openPfDialog("Max Sharpe", optimal.maxSharpe.weights, "#fbbf24", "max_sharpe")}
                  />
                  <OptimalPortfolioCard
                    title="Min Volatility"
                    subtitle="Lowest risk portfolio"
                    data={optimal.minVol}
                    color="#10b981"
                    stockStats={stockStats}
                    onCreatePortfolio={() => openPfDialog("Min Volatility", optimal.minVol.weights, "#10b981", "min_volatility")}
                  />
                </Box>
              )}

              {/* STOCK STATISTICS */}
              {running ? (
                <Paper sx={{ p: 3, bgcolor: theme.palette.background.paper, borderRadius: 2, border: paperBorder }}>
                  <Skeleton variant="text" width="40%" height={24} sx={{ bgcolor: isDark ? "#334155" : "var(--color-border-primary)", mb: 2 }} />
                  <Stack spacing={1}>{[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rectangular" height={50} sx={{ bgcolor: isDark ? "#1e293b" : "var(--color-bg-secondary)", borderRadius: 1 }} />)}</Stack>
                </Paper>
              ) : stockStats.length > 0 && (
                <Paper sx={{ p: 3, bgcolor: theme.palette.background.paper, borderRadius: 2, border: paperBorder }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                    <TrendingUpIcon sx={{ color: theme.palette.primary.main, fontSize: 30 }} />
                    <Typography variant="title" fontWeight="var(--font-weight-bold)" color={theme.palette.text.primary}>Individual Stock Statistics</Typography>
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {["STOCK", "PRICE", "RETURN", "VOLATILITY", "SHARPE"].map((h, i) => (
                            <TableCell key={h} align={i === 0 ? "left" : "right"} sx={{ color: theme.palette.text.secondary, fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", borderColor: isDark ? "#1e293b" : "var(--color-border-primary)" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stockStats.map(stat => (
                          <TableRow key={stat.ticker} sx={{ "&:hover": { bgcolor: isDark ? "#1e293b" : "var(--color-surface-hover)" } }}>
                            <TableCell sx={{ borderColor: isDark ? "#1e293b" : "var(--color-border-primary)" }}>
                              <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-bold)" color={theme.palette.text.primary}>{stat.ticker}</Typography>
                              <Typography fontSize="var(--font-size-sm)" color={theme.palette.text.secondary}>{stat.company_name}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ color: theme.palette.text.primary, fontSize: "var(--font-size-sm)", borderColor: isDark ? "#1e293b" : "var(--color-border-primary)" }}>{sym}{fmtL(stat.current_price, 2)}</TableCell>
                            <TableCell align="right" sx={{ borderColor: isDark ? "#1e293b" : "var(--color-border-primary)" }}>
                              <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color={stat.annualized_return > 0 ? "#10b981" : "#ef4444"}>{fmtL(stat.annualized_return * 100, 2)}%</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ color: theme.palette.text.primary, fontSize: "var(--font-size-sm)", borderColor: isDark ? "#1e293b" : "var(--color-border-primary)" }}>{fmtL(stat.annualized_volatility * 100, 2)}%</TableCell>
                            <TableCell align="right" sx={{ borderColor: isDark ? "#1e293b" : "var(--color-border-primary)" }}>
                              <Chip label={fmtL(stat.sharpe_ratio, 2)} size="small" sx={{ bgcolor: getSharpeColor(stat.sharpe_ratio), color: "white", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-bold)" }} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      {/* CREATE WATCHLIST DIALOG */}
      <Dialog open={isCreateWLDialogOpen} onClose={() => setIsCreateWLDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: theme.palette.background.paper, borderRadius: 2, border: paperBorder } }}>
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: paperBorder }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: isDark ? "#1e293b" : "var(--color-bg-secondary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AddIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography fontSize="var(--font-size-base)" fontWeight={700} color={theme.palette.text.primary}>Create New Watchlist</Typography>
              <Typography fontSize="var(--font-size-xs2)" color={theme.palette.text.secondary}>Organize your stocks into watchlists</Typography>
            </Box>
          </Stack>
        </Box>
        <Box sx={{ px: 2.5, py: 2.5 }}>
          <TextField fullWidth autoFocus placeholder="e.g., Tech Growth, Dividend Stocks" value={newWLName} onChange={e => setNewWLName(e.target.value)} onKeyPress={e => e.key === "Enter" && handleCreateWatchlist()} disabled={loading} size="small" />
        </Box>
        <Box sx={{ px: 2.5, pb: 2, display: "flex", justifyContent: "flex-end", gap: 1, borderTop: paperBorder, pt: 1.5 }}>
          <Button onClick={() => setIsCreateWLDialogOpen(false)} disabled={loading} sx={{ color: theme.palette.text.secondary, fontSize: 13, textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleCreateWatchlist} variant="contained" disabled={!newWLName.trim() || loading} sx={{ background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", fontSize: 13, fontWeight: 600, textTransform: "none" }}>
            {loading ? <CircularProgress size={16} color="inherit" /> : "Create Watchlist"}
          </Button>
        </Box>
      </Dialog>

      {/* CREATE PORTFOLIO FROM FRONTIER DIALOG */}
      <CreatePortfolioFromFrontier
        open={pfDialog.open}
        onClose={() => setPfDialog(p => ({ ...p, open: false }))}
        weights={pfDialog.weights}
        stockStats={stockStats}
        portfolioTitle={pfDialog.portfolioTitle}
        portfolioType={pfDialog.portfolioType}
        accentColor={pfDialog.accentColor}
        setSnackbar={setSnackbar}
        onCreated={() => setPfDialog(p => ({ ...p, open: false }))}
      />

      {/* SNACKBAR */}
      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity || "info"} variant="filled"
          sx={{ bgcolor: snackbar?.severity === "success" ? "#10b981" : snackbar?.severity === "error" ? "#ef4444" : snackbar?.severity === "warning" ? "#f59e0b" : "#3b82f6", fontSize: 12, fontWeight: 600 }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* ─────────────────────────────────────────────
   OPTIMAL PORTFOLIO CARD (RESPONSIVE FIX)
───────────────────────────────────────────── */
const OptimalPortfolioCard = ({ title, subtitle, data, color, stockStats, onCreatePortfolio }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const pieData = Object.entries(data.weights).map(([ticker, weight]) => ({ name: ticker, value: weight }));
  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
  const paperBorder = `1px solid ${isDark ? "#1e293b" : "var(--color-border-primary)"}`;

  return (
    <Paper sx={{
      p: { xs: 2, sm: 2.5, md: 3 },
      bgcolor: theme.palette.background.paper,
      borderRadius: 2,
      border: paperBorder,
      borderTop: `4px solid ${color}`,
      position: "relative",
      overflow: "hidden",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}>
      <Box sx={{ position: "absolute", top: -30, right: -30, width: 150, height: 150, background: `radial-gradient(circle, ${color}18 0%, transparent 70%)`, borderRadius: "50%", pointerEvents: "none" }} />

      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "flex-start" }, justifyContent: "space-between", mb: 2.5, gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "var(--font-size-lg)", fontWeight: "var(--font-weight-semibold)", color, letterSpacing: "0.04em", lineHeight: 1.3, wordBreak: "break-word" }}>
            {title}
          </Typography>
          <Typography fontSize="var(--font-size-base)" color={theme.palette.text.secondary} fontWeight={500} mt={0.5}>
            {subtitle}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={onCreatePortfolio}
          startIcon={<FolderIcon sx={{ fontSize: 14 }} />}
          sx={{
            fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", textTransform: "none",
            color, borderColor: color, whiteSpace: "nowrap", flexShrink: 0,
            px: 1.5, py: 0.5, minWidth: "auto",
            "&:hover": { bgcolor: `${color}14`, borderColor: color },
          }}
        >
          Create Portfolio
        </Button>
      </Box>

      <Box sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: { xs: 1, sm: 1.5, md: 2 },
        mb: 3,
      }}>
        {[
          { label: "Return", value: `${fmtL(data.expected_return * 100, 2)}%`, mColor: "#10b981" },
          { label: "Risk", value: `${fmtL(data.volatility * 100, 2)}%`, mColor: "#ef4444" },
          { label: "Sharpe", value: fmtL(data.sharpe_ratio, 2), mColor: color },
        ].map(m => (
          <Box key={m.label}
            sx={{
              textAlign: "center",
              p: { xs: 1, sm: 1.5, md: 2 },
              bgcolor: isDark ? "#1e293b" : "var(--color-bg-secondary)",
              borderRadius: 1.5,
              border: `1px solid ${isDark ? "#334155" : "var(--color-border-primary)"}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: { xs: 60, sm: 70, md: 80 },
              overflow: "hidden",
            }}
          >
            <Typography sx={{
              fontSize: { xs: 10, sm: 11, md: 12 },
              color: theme.palette.text.secondary,
              mb: 0.5,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}>
              {m.label}
            </Typography>
            <Typography sx={{
              fontSize: { xs: 16, sm: 18, md: 22 },
              fontWeight: 700,
              color: m.mColor,
              lineHeight: 1.2,
              wordBreak: "break-all",
            }}>
              {m.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderColor: isDark ? "#334155" : "var(--color-border-primary)", mb: 2.5 }} />

      <Typography sx={{
        fontSize: { xs: 12, sm: 13 },
        fontWeight: 700,
        color: theme.palette.text.primary,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        mb: 2,
      }}>
        Portfolio Allocation
      </Typography>

      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        alignItems: { xs: "center", md: "flex-start" },
        flex: 1,
        minHeight: 0,
      }}>
        <Box sx={{ width: { xs: 160, sm: 180 }, height: { xs: 160, sm: 180 }, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius="55%" outerRadius="85%" paddingAngle={3} dataKey="value">
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke={theme.palette.background.paper} strokeWidth={2} />)}
              </Pie>
              <RechartsTooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Stack spacing={1}>
            {pieData.map((item, i) => (
              <Box key={item.name} sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: { xs: 1, sm: 1.25 },
                bgcolor: isDark ? "#1e293b" : "var(--color-bg-secondary)",
                borderRadius: 1,
                border: `1px solid ${isDark ? "#334155" : "var(--color-border-primary)"}`,
                minWidth: 0,
              }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: COLORS[i % COLORS.length], borderRadius: "50%", flexShrink: 0, border: `2px solid ${theme.palette.background.paper}` }} />
                  <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: theme.palette.text.primary }} noWrap>
                    {item.name}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: theme.palette.text.primary, flexShrink: 0, ml: 1 }}>
                  {fmtL(item.value * 100, 1)}%
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};

export default EfficientFrontierSimulator;